import { Box, Breadcrumbs, Typography, useTheme } from "@mui/material";
import Label from "@/modules/common/components/ui-kit/label";
import CommonAvatar from "@/modules/common/components/ui-kit/common-avatar";
import { ArrowLeft, InfoIcon } from "lucide-react";
import useLeadInfo from "@/modules/lead-page/hooks/lead-info/lead-info.hook";
import { useLeadLabels } from "@/modules/lead-page/new/utils/lead-labels";
import { getLeadPhoneNumberFromGetLeadResponse } from "@/modules/lead-page/utils";
import { GetLeadResponse } from "@/modules/leads/dtos";
import StatusLabelSection from "@/modules/lead-page/new/components/sidebar/ui/status-labels";
import { Track } from "@/modules/property-tracks/types";
import ActionsCompact from "@/modules/lead-page/new/components/sidebar/ui/actions-compact";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import { LeadActions } from "@/modules/lead-page/new/components/header/lead-actions";
import { useNavigate } from "react-router-dom";
import { PathConstants } from "@/routes/constants";
import CopyButton from "@/modules/common/components/ui/copy-button";

interface ClientDetailsProps {
  track: Track | undefined;
  hasJourney?: boolean;
  onPersonClick: () => void;
}

const ClientDetails = ({
  track,
  hasJourney = true,
  onPersonClick,
}: ClientDetailsProps) => {
  const theme = useTheme();

  const navigate = useNavigate();

  const { lead } = useLeadInfo();

  const labels = useLeadLabels(lead);

  const doNotCall = lead?.data?.lead?.do_not_call;

  const isLeadActive = () => {
    const validityTime = lead?.data?.lead?.validity_time;
    if (!validityTime) return false;
    return new Date(validityTime) > new Date();
  };

  const phoneNumber = getLeadPhoneNumberFromGetLeadResponse(
    lead as GetLeadResponse
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: theme.nawy.spacing.sm,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xs,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <CommonIconButton
            variant="outlined-secondary"
            size="md"
            onClick={() => navigate(PathConstants.PROPERTY_TRACKS)}
          >
            <ArrowLeft
              size={16}
              style={{
                color: theme.nawy.colors.semantic.text.primary,
              }}
            />
          </CommonIconButton>
          {hasJourney && <LeadActions />}
        </Box>
        <Breadcrumbs
          separator="•"
          sx={{
            ...theme.nawy.typography.english.variants.headline.sm,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          {track?.property.propertyType && (
            <Typography sx={theme.nawy.typography.english.variants.headline.sm}>
              {track?.property.propertyType.name}
            </Typography>
          )}
          {track?.property.compound && (
            <Typography sx={theme.nawy.typography.english.variants.headline.sm}>
              {track?.property.compound.name}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.md.regular}
            >
              Property ID
            </Typography>
            <CopyButton
              text={track?.property.uid ?? ""}
              size={16}
              color={theme.nawy.colors.semantic.layer.accent}
            />
          </Box>
        </Breadcrumbs>
      </Box>

      {hasJourney && (
        <>
          {/* Tags Section */}
          <Box
            sx={{
              display: "flex",
              gap: theme.nawy.spacing.xs,
              flexWrap: "wrap",
            }}
          >
            <StatusLabelSection
              type={lead?.data?.lead_type?.lead_type}
              isActive={isLeadActive()}
            />
            {labels.map(({ key, ...props }) => (
              <Label key={key} {...props} />
            ))}
          </Box>

          {/* Owner Card */}
          <Box
            sx={{
              width: "100%",
              borderRadius: theme.nawy.spacing.sm,
              padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
              border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xs,
            }}
          >
            {/* Owner identity row */}
            <Box
              sx={{
                display: "flex",
                gap: theme.nawy.spacing.xs,
                alignItems: "center",
              }}
            >
              <CommonAvatar
                name={lead?.data.lead.name || "No Name"}
                size={32}
                color={theme.nawy.colors.primitives.cyan[600]}
                fontSize={{
                  fontSize:
                    theme.nawy.typography.english.variants.body.md.regular
                      .fontSize,
                }}
              />
              <Box
                sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
              >
                <Typography
                  sx={{
                    color: theme.nawy.colors.semantic.text.secondary,
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    lineHeight: 1.2,
                  }}
                >
                  Owner
                </Typography>
                <Box
                  onClick={onPersonClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: "3px",
                  }}
                >
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md
                        .semibold,
                      color: lead?.data.lead.name
                        ? theme.nawy.colors.semantic.text.accent
                        : theme.nawy.colors.semantic.text.tertiary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {lead?.data.lead.name || "No Name"}
                  </Typography>
                  <InfoIcon
                    style={{
                      width: "14px",
                      height: "14px",
                      strokeWidth: 2.5,
                      flexShrink: 0,
                      color: theme.nawy.colors.semantic.text.accent,
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Phone Number */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: theme.nawy.colors.primitives.white[200],
                padding: `6px ${theme.nawy.spacing.sm}`,
                borderRadius: theme.nawy.spacing.xs,
              }}
            >
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.primary,
                  ...theme.nawy.typography.english.variants.body.md.regular,
                }}
              >
                {phoneNumber ?? "Not set"}
              </Typography>
              <ActionsCompact
                phoneNumber={phoneNumber}
                doNotCall={!!doNotCall}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientDetails;
